'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';
import { countSubproductRailTooltipReports } from '@/domains/reports/utils/report-subproduct.utils';
import { useNebulyPagesForSubproduct } from '@/domains/pages/hooks/use-enabled-nebuly-pages';
import { useProjectReportsContext } from '@/providers/project-reports-provider';
import { useProjectsContext } from '@/providers/projects-provider';
import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { RAIL_POPUP_SIDE_OFFSET, RAIL_TOOLTIP_SURFACE_CLASS } from '@/components/layout/dashboard/app-shell-rail-product-styles';
import { cn } from '@/utils/shad';
export type AppShellRailTooltipContentProps = {
  title: string;
  description: string;
  icon?: LucideIcon;
  reportCount?: number;
  /** When set, report count is read from the selected project at render time. */
  subproduct?: ReportSubproduct;
};
export function AppShellRailTooltipContent(t0) {
  const $ = _c(19);
  const {
    title,
    description,
    icon: Icon,
    reportCount: reportCountProp,
    subproduct
  } = t0;
  const {
    selectedProject
  } = useProjectsContext();
  const {
    detailedReportsResp
  } = useProjectReportsContext();
  const nebulyPages = useNebulyPagesForSubproduct(subproduct);
  const nebulyPageCount = nebulyPages.length;
  detailedReportsResp?.reports;
  let t1;
  bb0: {
    if (reportCountProp !== undefined) {
      t1 = reportCountProp;
      break bb0;
    }
    if (!subproduct || !selectedProject) {
      t1 = undefined;
      break bb0;
    }
    const reports = detailedReportsResp?.reports;
    if (reports) {
      let t2;
      if ($[0] !== nebulyPageCount || $[1] !== reports || $[2] !== subproduct) {
        t2 = countSubproductRailTooltipReports(reports, subproduct, {
          nebulyPageCount
        });
        $[0] = nebulyPageCount;
        $[1] = reports;
        $[2] = subproduct;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
      break bb0;
    }
    t1 = selectedProject.nReportsBySubproduct?.[subproduct] ?? 0;
  }
  const reportCount = t1;
  let t2;
  if ($[4] !== Icon) {
    t2 = Icon ? <Icon className="size-[0.9375rem] shrink-0 text-muted-foreground" aria-hidden={true} /> : null;
    $[4] = Icon;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== title) {
    t3 = <span className="text-sm font-semibold text-foreground">{title}</span>;
    $[6] = title;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== reportCount) {
    t4 = reportCount !== undefined ? <span className="ml-auto shrink-0 rounded-full border border-border px-2 py-px font-mono text-[11px] text-muted-foreground">{reportCount} {reportCount === 1 ? "report" : "reports"}</span> : null;
    $[8] = reportCount;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex items-center gap-2">{t2}{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== description) {
    t6 = <p className="text-[13px] leading-normal text-muted-foreground">{description}</p>;
    $[14] = description;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="w-[14.75rem] space-y-2 text-left">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
type AppShellRailItemTooltipProps = Omit<ComponentPropsWithoutRef<typeof TooltipTrigger>, 'asChild' | 'children'> & {
  renderContent: () => ReactNode;
  children: ReactElement;
};

/**
 * Remaining props and the ref go to the tooltip trigger, so a rail item that also opens a popover
 * can nest this inside its `PopoverTrigger asChild` and keep the trigger on the button itself.
 */
export const AppShellRailItemTooltip = forwardRef<HTMLButtonElement, AppShellRailItemTooltipProps>(function AppShellRailItemTooltip(t0, ref) {
  const $ = _c(16);
  let children;
  let renderContent;
  let triggerProps;
  if ($[0] !== t0) {
    ({
      renderContent,
      children,
      ...triggerProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = renderContent;
    $[3] = triggerProps;
  } else {
    children = $[1];
    renderContent = $[2];
    triggerProps = $[3];
  }
  let t1;
  if ($[4] !== children || $[5] !== ref || $[6] !== triggerProps) {
    t1 = <TooltipTrigger asChild={true} ref={ref} {...triggerProps}>{children}</TooltipTrigger>;
    $[4] = children;
    $[5] = ref;
    $[6] = triggerProps;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("rounded-[10px] p-3.5 shadow-xl", RAIL_TOOLTIP_SURFACE_CLASS);
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== renderContent) {
    t3 = renderContent();
    $[9] = renderContent;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <TooltipContent side="right" align="center" sideOffset={RAIL_POPUP_SIDE_OFFSET} className={t2} variant="wide">{t3}</TooltipContent>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t4) {
    t5 = <Tooltip delayDuration={200} disableHoverableContent={true}>{t1}{t4}</Tooltip>;
    $[13] = t1;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
});